import React from "react";
import { Chapter } from "../components/Chapter";
import { Reveal } from "../components/Reveal";

const BENEFITS = [
  { title: "Save Time", lines: ["Less paperwork.", "More time leading your kitchen."] },
  { title: "Stay Organized", lines: ["Everything in one connected workflow."] },
  { title: "Reduce Waste", lines: ["Know what you have before you order."] },
  { title: "Standardize Operations", lines: ["One way of working.", "Every shift. Every kitchen."] },
  { title: "Better Decisions", lines: ["Real operational visibility.", "Not more spreadsheets."] },
];

export const SceneWhy = () => (
  <>
    {/* Why MiseOne exists — category definition, not features. */}
    <section
      id="why"
      data-scene
      className="relative px-6 pt-14 pb-8 md:px-12 md:pt-24 md:pb-14 lg:px-24"
    >
      <div className="mx-auto w-full max-w-[1600px]">
        <Chapter title="Why MiseOne exists" />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2
                className="font-display text-6xl font-black leading-[0.92] tracking-tighter text-[#1b1a18] sm:text-7xl"
                data-testid="why-headline"
              >
                Finally, software built for chefs.
              </h2>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.08}>
              <p className="font-body text-lg leading-[1.75] text-[#6b665e]">
                Restaurants already have software for{" "}
                <span className="text-[#1b1a18]">accounting</span>. For{" "}
                <span className="text-[#1b1a18]">POS</span>. For{" "}
                <span className="text-[#1b1a18]">payroll</span>. For{" "}
                <span className="text-[#1b1a18]">scheduling</span>. For{" "}
                <span className="text-[#1b1a18]">purchasing</span>.
              </p>
              <p className="mt-6 font-body text-lg leading-[1.75] text-[#6b665e]">
                But the people who run the kitchen have never had a system built around
                how they work.
              </p>
              <p className="mt-6 font-body text-lg font-medium text-[#EA6A43]">
                Until now.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Closing statement */}
        <Reveal delay={0.1}>
          <div className="mt-12 max-w-4xl md:mt-16">
            <p className="font-display text-3xl font-bold leading-[1.1] tracking-tight text-[#1b1a18] sm:text-4xl">
              MiseOne is the first operating system built around the daily work of
              chefs.
            </p>
            <p className="mt-5 font-body text-lg leading-relaxed text-[#9a958c] sm:text-xl">
              Not around accounting. Not around finance. Not around the office.
            </p>
            <p className="mt-2 font-display text-4xl font-black tracking-tighter text-[#EA6A43] sm:text-5xl">
              Around the kitchen.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* What makes MiseOne different — benefits, not modules. */}
    <section
      id="different"
      data-scene
      className="relative px-6 pt-6 pb-14 md:px-12 md:pt-8 md:pb-24 lg:px-24"
    >
      <div className="mx-auto w-full max-w-[1600px]">
        <div className="border-t border-[var(--mo-line)] pt-10 md:pt-16">
          <Chapter title="The difference" />
          <Reveal>
            <h2
              className="max-w-3xl font-display text-6xl font-black leading-[0.92] tracking-tighter text-[#1b1a18] sm:text-7xl"
              data-testid="different-headline"
            >
              What makes MiseOne different.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-x-16 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05}>
                <div data-testid={`benefit-${i}`}>
                  <span className="font-body text-[11px] font-semibold uppercase tracking-[0.28em] text-[#EA6A43]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="mt-4 font-display text-2xl font-bold tracking-tight text-[#1b1a18]">
                    {b.title}
                  </div>
                  <div className="mt-2 space-y-0.5">
                    {b.lines.map((l) => (
                      <p key={l} className="font-body text-[15px] leading-relaxed text-[#6b665e]">
                        {l}
                      </p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Positioning statement */}
          <Reveal delay={0.1}>
          <div className="mt-14 max-w-4xl md:mt-20">
              <p className="font-display text-3xl font-bold leading-[1.15] tracking-tight text-[#1b1a18] sm:text-4xl">
                Restaurants already have management software.
              </p>
              <p className="mt-2 font-display text-3xl font-bold leading-[1.15] tracking-tight text-[#1b1a18] sm:text-4xl">
                Chefs never had an operating system.
              </p>
              <p className="mt-4 font-display text-3xl font-black tracking-tighter text-[#EA6A43] sm:text-4xl">
                Until now.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </>
);
